// Onboarding API client.
// Wraps the /onboarding/* endpoints driven by web/scripts/onboarding.js.
// Replaces the legacy API_ONBOARDING_* constants from common.js.

const BASE = "/api/onboarding";

function authHeaders(bearer: string): HeadersInit {
  return { Authorization: `Bearer ${bearer}`, "X-Source": "web" };
}

function jsonHeaders(bearer: string): HeadersInit {
  return { ...authHeaders(bearer), "Content-Type": "application/json" };
}

async function toError(r: Response): Promise<Error> {
  let detail = "";
  try {
    const data = await r.json();
    detail = data?.detail || data?.message || JSON.stringify(data);
  } catch {
    detail = await r.text().catch(() => "");
  }
  const err = new Error(detail || `HTTP ${r.status}`);
  (err as Error & { status?: number }).status = r.status;
  return err;
}

async function jget<T>(bearer: string, path: string): Promise<T> {
  const r = await fetch(`${BASE}${path}`, { headers: authHeaders(bearer) });
  if (!r.ok) throw await toError(r);
  return (await r.json()) as T;
}

async function jpost<T>(bearer: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: jsonHeaders(bearer),
    body: JSON.stringify(body ?? {}),
  });
  if (!r.ok) throw await toError(r);
  return (await r.json()) as T;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DatasourceTile {
  id: number | string;
  name: string;
  description: string;
  db_type: string;
  logo?: string;
  available: boolean;
  already_added?: boolean;
  include_schemas?: string[] | null;
  org_context?: string;
}

export interface UserContext {
  description: string;
  fiscal_year_start_month: number;
  detect_choice_fields?: boolean;
}

export interface TableEntry {
  id?: number | string;
  table_name: string;
  schema_name?: string;
  row_count?: number;
  column_count?: number;
  description?: string;
  selected?: boolean;
}

export interface SchemaGroup {
  schema_name: string;
  tables: TableEntry[];
}

export interface ProgressPhase {
  phase?: string;
  message?: string;
  current?: number;
  total?: number;
}

export interface QAPair {
  question: string;
  sql?: string;
  description?: string;
  tables?: string[];
}

export interface ProgressResponse {
  job_id: string;
  status: "pending" | "running" | "done" | "failed";
  progress?: ProgressPhase;
  error?: string;
  qa_pairs?: QAPair[];
}

export interface StartResponse {
  job_id: string;
  status: string;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export const onboardingApi = {
  listDatasources(bearer: string): Promise<{ datasources: DatasourceTile[] }> {
    return jget(bearer, "/datasources");
  },

  listDatasourceTables(
    bearer: string,
    dsId: number | string,
    includeSchemas?: string[],
  ): Promise<{ schemas: SchemaGroup[] }> {
    const path = `/datasource_tables/${encodeURIComponent(String(dsId))}${
      includeSchemas && includeSchemas.length
        ? `?include_schemas=${encodeURIComponent(includeSchemas.join(","))}`
        : ""
    }`;
    return jget(bearer, path);
  },

  start(
    bearer: string,
    body: {
      datasource_id: number | string;
      selected_tables?: Array<{ schema_name: string; table_name: string }> | null;
      user_context: UserContext;
      include_schemas?: string[];
    },
  ): Promise<StartResponse> {
    return jpost(bearer, "/start", body);
  },

  progress(bearer: string, jobId: string): Promise<ProgressResponse> {
    return jget(bearer, `/progress/${encodeURIComponent(jobId)}`);
  },

  complete(bearer: string, jobId: string): Promise<{ status: string }> {
    return jpost(bearer, "/complete", { job_id: jobId });
  },

  logContact(bearer: string, body: { name?: string; email?: string; note?: string }): Promise<{ ok: boolean }> {
    return jpost(bearer, "/log_contact", body);
  },
};
